import React from 'react';

import { useIntl } from 'react-intl';

import { DataTableNewEntryProps } from 'src/registry/components/table/types';
import { generalMessages } from 'src/registry/utils/messages';
import { ICON_MAP } from 'src/utils/constants';
import { queryClient } from 'src/lib/react-query';
import { toast } from 'src/hooks/useToast';
import { useUpdateBillableMetric } from 'src/features/billableMetrics/api/useUpdateBillableMetric';
import { LIST_BILLABLE_METRICS_RESOURCE } from 'src/features/billableMetrics/api/useListBillableMetrics';
import { BILLABLE_METRIC_RESOURCE } from 'src/features/billableMetrics/api/useGetBillableMetric';
import { BillableMetric } from 'src/features/billableMetrics/utils/types';
import BillableMetricFormModal from 'src/features/billableMetrics/components/forms/BillableMetricFormModal';
import { billableMetricsMessages } from 'src/features/billableMetrics/utils/messages';

interface UpdateBillableMetricFormModalProps<TData> extends DataTableNewEntryProps<TData> {
    selectedBillableMetric: BillableMetric;
    children?: React.ReactNode;
}

function UpdateBillableMetricFormModal<TData>({
    className,
    semantics,
    children,
    selectedBillableMetric,
}: UpdateBillableMetricFormModalProps<TData>) {
    const { formatMessage } = useIntl();

    const { mutate, isPending } = useUpdateBillableMetric({
        onSuccess: ({ name }) => {
            void queryClient.invalidateQueries({ queryKey: [LIST_BILLABLE_METRICS_RESOURCE] });
            void queryClient.invalidateQueries({ queryKey: [BILLABLE_METRIC_RESOURCE] });
            toast({
                title: formatMessage(billableMetricsMessages.updateBillableMetricSuccess),
                description: formatMessage(billableMetricsMessages.updateBillableMetricSuccessDescription, {
                    entity: name,
                }),
            });
        },
    });

    return (
        <BillableMetricFormModal
            selectedBillableMetric={selectedBillableMetric}
            className={className}
            mutate={(values, onSuccess, onError) =>
                mutate({ ...values, metricId: selectedBillableMetric.id }, { onSuccess, onError })
            }
            title={formatMessage(generalMessages.editEntry, { entry: semantics?.entryName?.toLowerCase() })}
            icon={ICON_MAP.EDIT}
            isPending={isPending}
            subtitle={formatMessage(billableMetricsMessages.editBillableMetricSubtitle, {
                entity: selectedBillableMetric.name,
            })}
        >
            {children}
        </BillableMetricFormModal>
    );
}

export default UpdateBillableMetricFormModal;
